import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";

export default function IntroAnimation({ onDone }) {
  const [phase, setPhase] = useState(0); // 0 idle, 1 sparkle, 2 open, 3 done

  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 600);
    const t2 = setTimeout(() => setPhase(2), 1500);
    const t3 = setTimeout(() => setPhase(3), 2600);
    const t4 = setTimeout(() => onDone?.(), 3000);
    return () => [t1, t2, t3, t4].forEach(clearTimeout);
  }, [onDone]);

  const skip = () => onDone?.();

  return (
    <AnimatePresence>
      {phase < 3 && (
        <motion.div
          key="intro"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-[100] bg-[#0A0A0A] flex items-center justify-center overflow-hidden grain"
          data-testid="intro-animation"
        >
          {/* Radial gold glow */}
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1.1 }}
            transition={{ duration: 1.4, ease: "easeOut" }}
            className="absolute w-[60vmin] h-[60vmin] rounded-full"
            style={{
              background: "radial-gradient(circle, rgba(212,175,55,0.35) 0%, rgba(212,175,55,0) 65%)",
              filter: "blur(20px)",
            }}
          />

          {/* The Box */}
          <div className="relative" style={{ width: "min(280px, 60vw)", height: "min(180px, 40vw)" }}>
            {/* Box body */}
            <motion.div
              initial={{ y: 30, opacity: 0, scale: 0.85 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 rounded-[6px]"
              style={{
                background: "linear-gradient(145deg, #1a1a1a 0%, #050505 100%)",
                boxShadow:
                  "0 30px 60px -20px rgba(0,0,0,0.8), inset 0 0 0 1px rgba(212,175,55,0.35)",
              }}
            />
            {/* Gold ribbon vertical */}
            <div
              className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-[10px]"
              style={{
                background: "linear-gradient(180deg, #F3E5AB, #D4AF37 50%, #B8941F)",
                boxShadow: "0 0 20px rgba(212,175,55,0.45)",
              }}
            />
            {/* Gold ribbon horizontal */}
            <div
              className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[10px]"
              style={{
                background: "linear-gradient(90deg, #F3E5AB, #D4AF37 50%, #B8941F)",
                boxShadow: "0 0 20px rgba(212,175,55,0.45)",
              }}
            />
            {/* Lid (top half) */}
            <motion.div
              initial={{ rotateX: 0, y: 0 }}
              animate={phase >= 2 ? { rotateX: -110, y: -20, opacity: 0 } : {}}
              transition={{ duration: 0.9, ease: [0.65, 0, 0.35, 1] }}
              style={{
                position: "absolute",
                inset: 0,
                bottom: "50%",
                background: "linear-gradient(160deg, #1f1f1f 0%, #0a0a0a 100%)",
                transformOrigin: "top center",
                borderTop: "1px solid rgba(212,175,55,0.45)",
                boxShadow: "inset 0 -1px 0 rgba(212,175,55,0.25)",
              }}
            />

            {/* Brand mark */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="absolute inset-x-0 top-[14%] flex items-center justify-center pointer-events-none"
            >
              <div className="text-center">
                <div className="text-[10px] tracking-[0.5em] text-[#A3A3A3]">PRESENTS</div>
                <div className="font-serif-display text-3xl gold-text mt-1">Her Box</div>
              </div>
            </motion.div>

            {/* Inner glow when opening */}
            <AnimatePresence>
              {phase >= 2 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1.3 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.8 }}
                  className="absolute inset-0 rounded-[6px]"
                  style={{
                    background:
                      "radial-gradient(ellipse at center, rgba(243,229,171,0.95) 0%, rgba(212,175,55,0.5) 35%, rgba(212,175,55,0) 70%)",
                    filter: "blur(8px)",
                  }}
                />
              )}
            </AnimatePresence>
          </div>

          {/* Sparkles */}
          {phase >= 1 &&
            Array.from({ length: 18 }).map((_, i) => {
              const angle = (i / 18) * Math.PI * 2;
              const radius = 140 + Math.random() * 80;
              return (
                <motion.span
                  key={i}
                  initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
                  animate={{
                    x: Math.cos(angle) * radius,
                    y: Math.sin(angle) * radius,
                    opacity: [0, 1, 0],
                    scale: [0, 1, 0],
                  }}
                  transition={{ duration: 1.6, delay: i * 0.04, ease: "easeOut" }}
                  className="absolute w-1.5 h-1.5 rounded-full"
                  style={{
                    background: "#F3E5AB",
                    boxShadow: "0 0 8px #D4AF37, 0 0 16px #D4AF37",
                  }}
                />
              );
            })}

          {/* Skip */}
          <button
            onClick={skip}
            data-testid="intro-skip-btn"
            className="absolute bottom-8 right-8 text-[#A3A3A3] hover:text-[#D4AF37] text-xs tracking-[0.3em] uppercase transition-colors"
          >
            Skip
          </button>

          {/* Tagline at bottom */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: phase >= 1 ? 0.7 : 0, y: 0 }}
            transition={{ duration: 0.8 }}
            className="absolute bottom-16 inset-x-0 text-center text-[10px] tracking-[0.5em] uppercase text-[#A3A3A3]"
          >
            Unbox Your Power
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
